import { useCallback, useMemo } from 'react';
import { Bot, Plus } from 'lucide-react';
import { useTranslation } from '@aimsos/i18n';
import { Filters, Header, IconHighlight } from '@aimsos/ui';

/** Stable references — `Filters` is memoised, so inline objects re-render it every time. */
const VIEW_TOGGLE_HIDDEN = { hidden: true };
const NO_FILTERS = { status: [], type: [], category: [], createdWithin: '' };

/**
 * The page's sticky band: header + the filters that fold away under it.
 *
 * Replaces a bespoke mechanism — an `IntersectionObserver` on a sentinel, a
 * `ResizeObserver` + `requestAnimationFrame` loop keeping a `position: fixed`
 * bar pixel-aligned to the scroll container, and an app-local
 * `--header-compact-fade` CSS variable for its background. That variable is
 * declared in `apps/agents/src/index.css`, which is not loaded when the app runs
 * embedded in Core Shell, so the bar rendered fully transparent and the agent
 * cards scrolled straight through it.
 *
 * `position: sticky` does all of that natively. Scroll state comes from the page
 * (`useStickyScroll`), so this component stays presentational.
 *
 * See `docs/architecture/list-page/`.
 */
const AgentsListHeader = ({
  searchValue,
  onSearchChange,
  onSearchClear,
  onCreateClick,
  createLoading = false,
  filters,
  onFilterChange,
  onAllFiltersClick,
  isScrolled = false,
  showToolbar = true,
  onMouseEnter,
  onMouseLeave,
}) => {
  const { t } = useTranslation('agents');
  const statusOptions = useMemo(
    () => [
      { value: 'active', label: t('list.filterOptions.status.active') },
      { value: 'disabled', label: t('list.filterOptions.status.disabled') },
      { value: 'draft', label: t('list.filterOptions.status.draft') },
      { value: 'archived', label: t('list.filterOptions.status.archived') },
    ],
    [t],
  );
  const typeOptions = useMemo(
    () => [{ value: 'ai', label: t('list.filterOptions.type.ai') }],
    [t],
  );
  const categoryOptions = useMemo(
    () => [{ value: 'Service', label: t('list.filterOptions.category.Service') }],
    [t],
  );
  const createdWithinOptions = useMemo(
    () => [
      { value: 'today', label: t('list.filters.createdWithinOptions.today') },
      { value: 'last7days', label: t('list.filters.createdWithinOptions.last7days') },
      { value: 'last30days', label: t('list.filters.createdWithinOptions.last30days') },
      { value: 'last90days', label: t('list.filters.createdWithinOptions.last90days') },
    ],
    [t],
  );
  const handleSearchChange = useCallback(
    (e) => onSearchChange?.(e.target.value),
    [onSearchChange],
  );

  // `Filters` slots are array-valued even for single-select, so the scalar
  // `createdWithin` is adapted here at the boundary rather than stored as one.
  const filterSlots = useMemo(
    () => [
      {
        id: 'status',
        label: t('list.filters.status'),
        value: filters.status ?? [],
        onChange: (next) => onFilterChange?.({ ...filters, status: next }),
        options: statusOptions,
      },
      {
        id: 'type',
        label: t('list.filters.type'),
        value: filters.type ?? [],
        onChange: (next) => onFilterChange?.({ ...filters, type: next }),
        options: typeOptions,
      },
      {
        id: 'category',
        label: t('list.filters.category'),
        value: filters.category ?? [],
        onChange: (next) => onFilterChange?.({ ...filters, category: next }),
        options: categoryOptions,
      },
      {
        // Labelled "Created", not "Last Update" (CT-5291): the list endpoint
        // only filters on `created_at` — `updated_at` is accepted for ordering
        // but there is no filter for it — so a range labelled "last update"
        // could never do what it says.
        id: 'createdWithin',
        label: t('list.filters.createdWithin'),
        multiple: false,
        value: filters.createdWithin ? [filters.createdWithin] : [],
        onChange: (next) => onFilterChange?.({ ...filters, createdWithin: next?.[0] ?? '' }),
        options: createdWithinOptions,
      },
    ],
    [filters, onFilterChange, t, statusOptions, typeOptions, categoryOptions, createdWithinOptions],
  );

  const handleClearAll = useCallback(() => {
    onFilterChange?.(NO_FILTERS);
    onSearchClear?.();
  }, [onFilterChange, onSearchClear]);

  const searchProps = useMemo(
    () => ({
      value: searchValue,
      onChange: handleSearchChange,
      onClear: onSearchClear,
      placeholder: t('list.searchPlaceholder'),
    }),
    [searchValue, handleSearchChange, onSearchClear, t],
  );

  const filtersI18n = useMemo(
    () => ({
      clearFilters: t('list.filters.clearAll'),
      allFilters: t('list.filters.allFilters'),
    }),
    [t],
  );

  return (
    <div
      className="sticky top-0 z-20 px-6"
      onMouseEnter={isScrolled ? onMouseEnter : undefined}
      onMouseLeave={isScrolled ? onMouseLeave : undefined}
    >
      {/* Vertical padding belongs on the PAINTED element, never on the sticky
          wrapper: outside it the backdrop stops at the header's own box and the
          cards scroll visibly through the gutter above and below it. */}
      <div
        className={['pb-6 pt-6 transition-colors duration-200', isScrolled ? 'bg-surface-base' : '']
          .filter(Boolean)
          .join(' ')}
      >
        <Header
          size={isScrolled ? 'compress' : 'M'}
          showIcon
          icon={<IconHighlight icon={Bot} bgColor="Primary" iconColor="PrimaryAlt" size="small" />}
          title={t('list.title')}
          showBackButton={false}
          showDescription={!isScrolled}
          description={t('list.subtitle')}
          showTag={false}
          // `size: 'md'` on purpose — at header size `M` the CTA would inherit
          // `sm` and come out smaller than every other list page's.
          primaryCta={{
            label: t('list.createButton'),
            onClick: onCreateClick,
            leftIcon: Plus,
            size: 'md',
            disabled: createLoading,
            loading: createLoading,
          }}
        />

        {/* Folded by CSS rather than unmounted, so the fold has something to
            animate from and the filter state survives. `inert` keeps the hidden
            toolbar out of the tab order; `overflow` is hidden ONLY while
            collapsed, or it clips the filter dropdowns. */}
        <div
          inert={showToolbar ? undefined : ''}
          className={[
            'transition-all duration-200',
            showToolbar
              ? 'mt-4 max-h-40 overflow-visible opacity-100'
              : 'mt-0 max-h-0 overflow-hidden opacity-0',
          ].join(' ')}
        >
          {/* The design-system `Filters` bar — the same component every other
              list page uses, never a hand-rolled row of selects.

              "All filters" is NOT optional here: `Filters` renders zero chips
              below 1280px and expects the slideout to carry every dimension
              (see filtersResponsive.js). Without it the filters are unreachable
              on any narrower screen. */}
          <Filters
            searchProps={searchProps}
            filters={filterSlots}
            maxFilters={4}
            sort={null}
            viewToggle={VIEW_TOGGLE_HIDDEN}
            onAllFiltersClick={onAllFiltersClick}
            onClearAll={handleClearAll}
            i18n={filtersI18n}
          />
        </div>
      </div>

      {/* Cards have to leave somewhere. Without this they reappear at full
          opacity a pixel under a hard edge, and the bar reads as clipping the
          list rather than floating over it. */}
      {isScrolled ? (
        <div
          aria-hidden
          className="pointer-events-none h-10 bg-gradient-to-b from-surface-base to-transparent"
        />
      ) : null}
    </div>
  );
};

export default AgentsListHeader;
